import { FC, useEffect, useState } from 'react'

import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { isProductProtected } from 'featureSliced/shared/utils/isProductProtected'
import { getProtectedAge } from 'featureSliced/shared/utils/getProtectedAge'
import { RenderIf } from 'components/RenderIf'

import { useOpenNote } from 'featureSliced/shared/hooks/openModals/useOpenNote'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'

import { IconFilled } from 'components/IconsContainers/styled'
import { ReactComponent as ClocheImage } from 'assets/cloches/clocheSquare.svg'
import heartIcon from 'assets/hearts/empty_l.svg'
import noteIcon from 'assets/notes/large_note.svg'

import {
  ButtonsContainer,
  ProductMainImage,
  ProductMainImageContainer,
  SecondaryImage,
} from './styled'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { WishlistSlice } from 'featureSliced/shared/store/reducers/WishlistReducer'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'

type ProductImageProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
}

export const ProductImage: FC<ProductImageProps> = ({
  product,
  productTemplateId,
}) => {
  // Hooks
  const openNote = useOpenNote()
  const dispatch = useMyDispatch()
  const wishlist = useMySelector((state) => state.wishlist)
  const addOrRemoveFromWishlist = WishlistSlice.actions.addOrRemoveFromWishlist

  // Store
  const color = useMySelector((state) => state.app.interfaceStyles.colour)
  const notesAvailable = useMySelector((state) => state.app.menu.notesAvailable)

  // State
  const [isImgErrored, setIsImageErrored] = useState(false)
  const [isWishlisted, setIsWishlisted] = useState<boolean>(false)

  //useEffects
  useEffect(() => {
    const isSelected = !!wishlist?.find(
      (listItem) =>
        listItem.id === product.id &&
        listItem.productTemplateId === productTemplateId
    )
    setIsWishlisted(isSelected)
  }, [product.id])

  // Functions
  const handleOpenNote = (productId: number, categoryId: number) => {
    if (notesAvailable === 1) {
      openNote(productId, categoryId)
    }
  }

  const handleWishListClick = (productId: number) => {
    // handleWishlist({ id: productId, productTemplateId })
    dispatch(addOrRemoveFromWishlist({ id: productId, productTemplateId }))
    setIsWishlisted((prevValue) => !prevValue)
  }

  return (
    <ProductMainImageContainer>
      {/* IMAGE */}
      <RenderIf condition={!isImgErrored}>
        <ProductMainImage
          src={product.imageUrl || 'ERRORED_IMAGE'}
          alt="product"
          onError={({ currentTarget }) => {
            setIsImageErrored(true)
            currentTarget.onerror = null // prevents looping
          }}
        />
      </RenderIf>
      <RenderIf condition={isImgErrored}>
        <IconFilled color={`${color}80`}>
          <ClocheImage />
        </IconFilled>
      </RenderIf>
      {/* BUTTONS */}
      <ButtonsContainer>
        {/* 1 */}
        <RenderIf condition={location.pathname === '/menu'}>
          <SecondaryImage
            onClick={() => handleWishListClick(product.id)}
            isActive={isWishlisted}
          >
            <img alt="wishlist" src={heartIcon} />
          </SecondaryImage>
        </RenderIf>
        {/* 2 */}
        <RenderIf condition={isProductProtected(product)}>
          <SecondaryImage>{getProtectedAge(product)}</SecondaryImage>
        </RenderIf>
        {/* 3 */}
        <RenderIf
          condition={notesAvailable === 1 && location.pathname !== '/menu'}
        >
          <SecondaryImage
            onClick={() => handleOpenNote(product.id, product.categoryId)}
          >
            <img alt="note" src={noteIcon} />
          </SecondaryImage>
        </RenderIf>
      </ButtonsContainer>
    </ProductMainImageContainer>
  )
}
